import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { GraduationCap, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import WorkspaceLayout from '@/components/WorkspaceLayout';
import { PARENT_NAV } from '@/lib/workspaceNav';
import { useInternshipAlerts } from '@/lib/useInternshipAlerts';
import ParentExperiences from '@/pages/parent/ParentExperiences';
import ParentInternships from '@/pages/parent/ParentInternships';
import ChildCard from '@/pages/parent/ChildCard';
import {
  NeedsAttention, UpcomingExperiences, ContinueExploring, SectionHeading,
} from '@/pages/parent/ParentHomeSections';

export default function ParentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const section = location.pathname.startsWith('/parent/children') ? 'children'
    : location.pathname.startsWith('/parent/experiences') ? 'experiences'
    : location.pathname.startsWith('/parent/internships') ? 'internships' : 'home';

  const { items: internshipAlerts } = useInternshipAlerts();
  const [children, setChildren] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [schoolName, setSchoolName] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cr, br] = await Promise.all([api.get('/children'), api.get('/bookings')]);
      setChildren(cr.data);
      setBookings(br.data);
    } catch {
      toast.error('Could not load your family data. Please refresh.');
    }
    setLoading(false);
  }, []);

  const fetchSchool = useCallback(async () => {
    if (!user?.school_id) return;
    try {
      const r = await api.get(`/schools/${user.school_id}`);
      setSchoolName(r.data.school_name);
    } catch { setSchoolName(user.school_id); }
  }, [user?.school_id]);

  useEffect(() => { fetchAll(); fetchSchool(); }, [fetchAll, fetchSchool]);

  const firstName = (user?.full_name || '')
    .replace(/^(Dr\.?|Mr\.?|Mrs\.?|Ms\.?|Prof\.?)\s+/i, '').split(' ')[0];

  const childrenGrid = loading ? (
    <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#F97D6A]" /></div>
  ) : children.length === 0 ? (
    <div className="rounded-2xl border border-dashed border-[#E5E7EB] p-10 text-center" data-testid="empty-children">
      <GraduationCap className="h-7 w-7 text-gray-200 mx-auto mb-3" />
      <p className="text-sm font-semibold text-[#1F2937] font-['Outfit'] mb-1">No student profile linked yet</p>
      <p className="text-sm text-[#9CA3AF]">Please contact your Career Coordinator to link your child's profile.</p>
    </div>
  ) : (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="children-grid">
      {children.map(child => <ChildCard key={child.id} child={child} bookings={bookings} schoolName={schoolName} />)}
    </div>
  );

  return (
    <WorkspaceLayout nav={PARENT_NAV} activeRole="parent" testId="parent-dashboard">
      {section === 'home' && (
        <div data-testid="parent-home">
          {/* Welcome */}
          <div className="mb-9" data-testid="parent-greeting">
            <h1 className="text-3xl sm:text-4xl font-bold text-[#1F2937] font-['Outfit'] tracking-tight" data-testid="parent-title">
              Welcome back, {firstName}
            </h1>
          </div>

          <NeedsAttention children={children} bookings={bookings} internshipAlerts={internshipAlerts} />

          <section className="mb-10" data-testid="my-children">
            <SectionHeading
              title={children.length === 1 ? 'My child' : 'My children'}
              action={children.length > 1 ? 'View all' : null}
              to="/parent/children"
              testId="view-all-children"
            />
            {childrenGrid}
          </section>

          <UpcomingExperiences bookings={bookings} />
          <ContinueExploring />
        </div>
      )}

      {section === 'children' && (
        <div data-testid="parent-children-section">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1F2937] font-['Outfit'] tracking-tight mb-2" data-testid="dashboard-section-title">
            My Children
          </h1>
          <p className="text-sm text-[#6B7280] mb-7">
            {loading ? 'Loading students…' : `${children.length} ${children.length === 1 ? 'student' : 'students'} linked at ${schoolName || user?.school_id}`}
          </p>
          {childrenGrid}
        </div>
      )}

      {section === 'internships' && (
        <div data-testid="parent-internships-section">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1F2937] font-['Outfit'] tracking-tight mb-2" data-testid="dashboard-section-title">
            Internships
          </h1>
          <p className="text-sm text-[#6B7280] mb-7">
            Short placements with companies working with {schoolName || 'your school'}.
          </p>
          <ParentInternships />
        </div>
      )}

      {section === 'experiences' && (
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1F2937] font-['Outfit'] tracking-tight mb-7" data-testid="dashboard-section-title">
            Experiences
          </h1>
          <ParentExperiences bookings={bookings} />
        </div>
      )}
    </WorkspaceLayout>
  );
}
